import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
} from "@mui/material";
import UploadBox from "../fileUploader/UploadBox";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import UploaderLoadingComponent from "./UploaderLoadingComponent";
import { useDispatch, useSelector } from "react-redux";
import {
  handleLoadDocument,
  updateEditingForm,
  updatePackageAndSelect,
  updateSelectedPackageFormObject,
  uploadUserPackagePDF,
} from "../../store/actions/userPackagesActions";
import RenderedPackageFormFields from "./RenderedPackageFormFields";

const steps = ["Upload Document", "Document Details"];

const AddingDocumentStepper = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  setAddingNewDocument,
  uploadError,
  setUploadError,
  setPackagePanelOpen,
}) => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState(0);
  const [uploaderLoading, setUploaderLoading] = useState(false);

  const { selectedPackage, selectedPackageForm } = useSelector(
    (state) => state.userPackages
  );

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      setPackagePanelOpen(false);
      setAddingNewDocument(false);
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      setAddingNewDocument(false);
      dispatch(updateSelectedPackageFormObject(selectedPackage?.documents[0]));
    } else {
      return;
    }
  };

  const handleExtractPDF = async () => {
    const response = await dispatch(
      uploadUserPackagePDF(
        currentProcessingFile,
        setUploaderLoading,
        "",
        selectedPackage?.packageID // ✅ packageID <-- Existing Package
      )
    );
    if (response) {
      const packageCopy = {
        ...selectedPackage,
        documents: [...selectedPackage.documents, response],
      };

      dispatch(updatePackageAndSelect(packageCopy));
      dispatch(updateSelectedPackageFormObject(response));
      dispatch(
        updateEditingForm({
          ...packageCopy,
          document: response,
        })
      );
      await dispatch(
        handleLoadDocument(setUploaderLoading, setCurrentProcessingFile)
      );
      setAddingNewDocument(false);
      setActiveStep(0);
    }
  };

  useEffect(() => {
    if (uploaderLoading && uploaderLoading === true) {
      handleExtractPDF();
      //   const timer = setTimeout(() => {
      //     handleExtractPDF();
      //   }, 1000);
      //   return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploaderLoading]);

  return (
    <>
      {/* Fixed Header with Stepper */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          borderBottom: "3px solid #e0e0e0",
        }}
      >
        <Typography mb={1} align="center" variant="h4" color="primary">
          {selectedPackage?.packageName}
        </Typography>
        <Typography mb={1} align="center" variant="h6" color="primary">
          Add New Document
        </Typography>

        {/* MUI Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2, py: 2 }}>
        {activeStep === 0 && (
          <>
            <Box mt={2}>
              {uploaderLoading ? (
                <UploaderLoadingComponent />
              ) : (
                <UploadBox
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  uploadError={uploadError}
                  setUploadError={setUploadError}
                  setUploaderLoading={setUploaderLoading}
                />
              )}

              {!uploaderLoading &&
                uploadError === "File does not appear to be a valid PDF" && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        Why am I seeing this?
                      </Typography>
                      <HelpOutlineIcon fontSize="small" color="action" />
                    </Box>

                    <ul style={{ paddingLeft: "1.2rem", marginTop: "0.5rem" }}>
                      <li>
                        The file is not a real PDF (e.g., renamed from Word or
                        image file).
                      </li>
                      <li>
                        The PDF is damaged or has missing internal structure.
                      </li>
                      <li>
                        The file may be encrypted or generated from an
                        unsupported source.
                      </li>
                    </ul>
                  </Box>
                )}
            </Box>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Typography mb={2} variant="h5" color="primary" align="center">
              You can edit these fields later
            </Typography>
            <RenderedPackageFormFields
              selectedPackage={selectedPackage}
              selectedPackageForm={selectedPackageForm}
              firstLoad={true}
            />
          </>
        )}
      </Box>

      {/* Fixed Back-Next Buttons at the Bottom */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button
          disabled={uploaderLoading}
          variant="contained"
          sx={{ visibility: activeStep > 0 && "hidden" }}
          color={activeStep === 0 ? "error" : "secondary"}
          onClick={handleBack}
        >
          {activeStep === 0 ? "Cancel" : "Back"}
        </Button>
        <Button
          variant="contained"
          color={activeStep === steps.length - 1 ? "success" : "primary"}
          onClick={handleNext}
          disabled={activeStep === 0 || uploaderLoading}
          sx={{
            visibility: activeStep === 0 && "hidden",
            width: activeStep === steps.length - 1 ? "15rem" : undefined,
          }}
        >
          {activeStep === steps.length - 1 ? "Complete" : "Next"}
        </Button>
      </Box>
    </>
  );
};

export default AddingDocumentStepper;
